import axios from "axios";
import store from "../../store/store";
import {
  getStoredAdminAuthContext,
  getStoredTeacherToken,
  normalizeTeacherToken,
} from "./teacherStorage";
import { expireTeacherSession } from "./teacherSlice";
import { formatTeacherYearLabels } from "../../utils/teacherYears";
import { repartitionBookings } from "../../utils/bookingStatus";
import { logTeacherApiResult } from "../../utils/datadogLogger";

const DEFAULT_TEACHER_API_BASE_URL = "http://127.0.0.1:8000/api";
const PUBLIC_TEACHER_READ_TIMEOUT_MS = 0;
const META_KEYS = new Set(["status", "status_code", "message", "detail", "success"]);
const AUTH_ERROR_PATTERNS = [
  /token expired/i,
  /jwt token is expired/i,
  /jwt token is not valid/i,
  /authentication credentials were not provided/i,
  /not authenticated/i,
  /unauthorized/i,
  /invalid token/i,
];

const formatConfiguredApiBaseUrl = (configuredBaseUrl = "") => {
  const trimmedBaseUrl = String(configuredBaseUrl || "").trim();
  if (!trimmedBaseUrl) return DEFAULT_TEACHER_API_BASE_URL;

  let sanitizedBaseUrl = trimmedBaseUrl.replace(/\/+$/, "");

  // Prevent mixed-content failures on HTTPS pages when env accidentally points to HTTP API.
  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    /^http:\/\//i.test(sanitizedBaseUrl)
  ) {
    sanitizedBaseUrl = String(window.location.origin || "").replace(/\/+$/, "");
  }

  if (sanitizedBaseUrl.endsWith("/api")) return sanitizedBaseUrl;

  return `${sanitizedBaseUrl}/api`;
};

export const getTeacherApiBaseUrl = () =>
  formatConfiguredApiBaseUrl(process.env.REACT_APP_BASE_URL);

const teacherApiClient = axios.create({
  baseURL: getTeacherApiBaseUrl(),
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

teacherApiClient.interceptors.request.use((config) => {
  const nextHeaders = { ...(config.headers || {}) };

  if (config.teacherAuth) {
    const teacherToken = normalizeTeacherToken(getStoredTeacherToken());
    if (teacherToken) {
      nextHeaders.Authorization = `Bearer ${teacherToken}`;
    }
  }

  if (config.adminAuth) {
    const adminContext =
      typeof config.adminAuth === "object"
        ? config.adminAuth
        : getStoredAdminAuthContext();

    const adminAuthToken = normalizeTeacherToken(adminContext?.authToken || "");
    const adminJwt = normalizeTeacherToken(adminContext?.jwt || "");

    if (adminAuthToken) {
      nextHeaders.Authorization = `Token ${adminAuthToken}`;
    }

    if (adminJwt) {
      nextHeaders.HTTP_JWT = adminJwt;
    }
  }

  config.headers = nextHeaders;
  return config;
});

teacherApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.config?.teacherAuth && isTeacherAuthError(error)) {
      store.dispatch(expireTeacherSession());
    }

    return Promise.reject(error);
  }
);

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const toTrimmedString = (value) =>
  value === null || typeof value === "undefined" ? "" : String(value).trim();

const pickFirstTeacherValue = (teacher = {}, keys = []) =>
  keys.map((key) => toTrimmedString(teacher?.[key])).find(Boolean) || "";

const toMessageList = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => toMessageList(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).flatMap((item) => toMessageList(item));
  }

  if (value === null || typeof value === "undefined") return [];
  return [String(value)];
};

const findByMatcher = (source, matcher, visited = new WeakSet()) => {
  if (!source || typeof source !== "object") return undefined;
  if (visited.has(source)) return undefined;
  visited.add(source);

  if (Array.isArray(source)) {
    for (const item of source) {
      const result = findByMatcher(item, matcher, visited);
      if (typeof result !== "undefined") return result;
    }
    return undefined;
  }

  for (const [key, value] of Object.entries(source)) {
    if (matcher(key, value)) return value;
  }

  for (const value of Object.values(source)) {
    const nestedResult = findByMatcher(value, matcher, visited);
    if (typeof nestedResult !== "undefined") return nestedResult;
  }

  return undefined;
};

const extractTeacherErrorMessage = (payload, fallbackMessage = "") =>
  toMessageList(payload?.detail || payload?.message || payload?.error)[0] ||
  toMessageList(payload?.non_field_errors)[0] ||
  fallbackMessage ||
  "";

const collectTeacherFieldErrors = (source, target, parentKey = "") => {
  if (!isPlainObject(source)) {
    return;
  }

  Object.entries(source).forEach(([key, value]) => {
    if (META_KEYS.has(key)) {
      if (isPlainObject(value)) {
        collectTeacherFieldErrors(value, target, parentKey);
      }
      return;
    }

    const nextKey = parentKey || key;

    if (isPlainObject(value)) {
      collectTeacherFieldErrors(value, target, nextKey);
      return;
    }

    const messages = toMessageList(value);
    if (messages.length) {
      target[nextKey] = messages.join(" ");
    }
  });
};

export const isTeacherAuthError = (error) => {
  const responseStatus = error?.response?.status || error?.status || 0;
  if (responseStatus === 401 || responseStatus === 403) {
    return true;
  }

  const rawPayload = error?.response?.data ?? error?.raw ?? {};
  const candidateMessages = [
    extractTeacherErrorMessage(rawPayload, ""),
    error?.message || "",
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  return candidateMessages.some((message) =>
    AUTH_ERROR_PATTERNS.some((pattern) => pattern.test(message))
  );
};

export const humanizeTeacherField = (fieldName = "") =>
  String(fieldName)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const formatTeacherValue = (value) => {
  if (Array.isArray(value)) {
    const formatted = value.map((item) => formatTeacherValue(item)).filter(Boolean);
    return formatted.length ? formatted.join(", ") : "Not provided";
  }

  if (isPlainObject(value)) {
    return JSON.stringify(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value === null || typeof value === "undefined" || value === "") {
    return "Not provided";
  }

  return String(value);
};

export const getTeacherDisplayName = (teacher = {}) => {
  const fullName = [
    teacher?.name,
    teacher?.full_name,
    teacher?.fullName,
    teacher?.teacher_name,
    teacher?.teacherName,
    [teacher?.first_name, teacher?.last_name].filter(Boolean).join(" ").trim(),
    [teacher?.firstname, teacher?.lastname].filter(Boolean).join(" ").trim(),
  ].find((candidate) => String(candidate || "").trim());

  return String(fullName || "").trim() || "Teacher";
};

export const getTeacherEmail = (teacher = {}) =>
  String(
    teacher?.email ||
      teacher?.email_address ||
      teacher?.emailAddress ||
      teacher?.mail ||
      teacher?.teacher_email ||
      teacher?.teacherEmail ||
      teacher?.tutor_email ||
      teacher?.tutorEmail ||
      teacher?.user?.email ||
      teacher?.profile?.email ||
      teacher?.teacher?.email ||
      teacher?.tutor?.email ||
      ""
  ).trim();

export const getTeacherPhotoValue = (teacher = {}) =>
  pickFirstTeacherValue(teacher, [
    "photoImage",
    "photo",
    "profile_photo",
    "profilePhoto",
    "image",
    "avatar",
  ]);

export const getTeacherCertificateValue = (teacher = {}) =>
  pickFirstTeacherValue(teacher, [
    "certificateImage",
    "certificate_image",
    "certificate",
    "certificate_url",
  ]);

export const getTeacherIdentifier = (teacher = {}) =>
  String(
    teacher?.id ||
      teacher?.uuid ||
      teacher?.teacher_id ||
      teacher?.teacherId ||
      teacher?.tutor_id ||
      teacher?.tutorId ||
      ""
  ).trim();

export const extractTeacherToken = (payload) =>
  normalizeTeacherToken(
    String(
      findByMatcher(payload, (key, value) => {
        if (typeof value !== "string") return false;
        return ["jwtoken", "jwt", "token", "access", "access_token"].includes(
          String(key).toLowerCase()
        );
      }) || ""
    )
  );

export const extractPreAuthToken = (payload) =>
  String(
    findByMatcher(payload, (key, value) => {
      if (typeof value !== "string") return false;
      return ["pre_auth_token", "preauthtoken"].includes(
        String(key).replace(/[^a-z]/gi, "").toLowerCase()
      );
    }) || ""
  ).trim();

export const extractTeacherRecord = (payload, fallbackTeacher = {}) => {
  const hasTeacherRecordShape = (candidate) =>
    isPlainObject(candidate) &&
    ["email", "name", "full_name", "phone", "experience", "rating"].some((key) =>
      Object.prototype.hasOwnProperty.call(candidate, key)
    );

  const nestedTeacher = findByMatcher(payload, (key, value) => {
    if (!isPlainObject(value)) return false;
    return ["teacher", "tutor", "profile", "user"].includes(
      String(key).toLowerCase()
    );
  });
  const nestedDataTeacher = hasTeacherRecordShape(payload?.data) ? payload.data : null;

  const normalizeTeacherRecord = (teacher = {}) => {
    const photoImage = getTeacherPhotoValue(teacher);
    const certificateImage = getTeacherCertificateValue(teacher);
    const yearSource = (() => {
      if (Array.isArray(teacher?.year_labels) && teacher.year_labels.length) {
        return teacher.year_labels;
      }
      if (String(teacher?.year_labels || "").trim()) return teacher.year_labels;
      if (Array.isArray(teacher?.year) && teacher.year.length) return teacher.year;
      if (String(teacher?.year || "").trim()) return teacher.year;
      return "";
    })();
    const yearLabels = formatTeacherYearLabels(yearSource);

    return {
      ...(teacher || {}),
      ...(photoImage ? { photoImage } : {}),
      ...(certificateImage ? { certificateImage } : {}),
      ...(yearLabels.length ? { year_labels: yearLabels } : {}),
    };
  };

  if (nestedTeacher) {
    return normalizeTeacherRecord({ ...(fallbackTeacher || {}), ...nestedTeacher });
  }

  if (nestedDataTeacher) {
    return normalizeTeacherRecord({
      ...(fallbackTeacher || {}),
      ...nestedDataTeacher,
    });
  }

  if (hasTeacherRecordShape(payload)) {
    return normalizeTeacherRecord({ ...(fallbackTeacher || {}), ...payload });
  }

  return fallbackTeacher && Object.keys(fallbackTeacher).length
    ? normalizeTeacherRecord(fallbackTeacher)
    : null;
};

const findFirstArray = (payload, preferredKeys = []) => {
  for (const preferredKey of preferredKeys) {
    const value = payload?.[preferredKey];
    if (Array.isArray(value)) return value;
  }

  return findByMatcher(payload, (_key, value) => Array.isArray(value));
};

export const normalizeTeacherCollection = (payload) => {
  if (Array.isArray(payload)) return payload;

  const list =
    findFirstArray(payload, ["teachers", "tutors", "results", "items", "data"]) || [];

  return Array.isArray(list) ? list : [];
};

export const normalizeAvailabilityCollection = (payload) => {
  if (Array.isArray(payload)) return payload;

  const list =
    findFirstArray(payload, ["availability", "slots", "results", "items", "data"]) || [];

  if (Array.isArray(list)) return list;

  if (isPlainObject(payload)) {
    return Object.entries(payload).flatMap(([key, value]) => {
      if (!Array.isArray(value)) return [];

      return value.map((item) =>
        isPlainObject(item) ? item : { date: key, label: formatTeacherValue(item) }
      );
    });
  }

  return [];
};

export const normalizeTeacherBookingPayload = (payload) => {
  const data = isPlainObject(payload?.data) ? payload.data : payload;
  const summary = isPlainObject(data?.summary) ? data.summary : {};
  const repartitioned = repartitionBookings({
    upcoming: Array.isArray(data?.upcoming) ? data.upcoming : [],
    past: Array.isArray(data?.past) ? data.past : [],
  });

  return {
    summary: {
      upcoming: Number(summary?.upcoming) || repartitioned.summary.upcoming,
      past: Number(summary?.past) || repartitioned.summary.past,
      total: Number(summary?.total) || repartitioned.summary.total,
    },
    upcoming: repartitioned.upcoming,
    past: repartitioned.past,
  };
};

export const normalizeTeacherError = (error, requestConfig = {}) => {
  const responseStatus = error?.response?.status ?? error?.status ?? 0;
  const errorCode = String(error?.code || "").trim();
  const errorMessage = String(error?.message || "").trim();
  const isTimeout =
    errorCode.toUpperCase() === "ECONNABORTED" ||
    /timeout|timed out/i.test(errorMessage);
  const isNetworkError =
    responseStatus === 0 &&
    (!error?.response ||
      errorCode.toUpperCase() === "ERR_NETWORK" ||
      /network error|failed to fetch|load failed/i.test(errorMessage));
  const rawPayload = error?.response?.data ?? error?.raw ?? {};
  const fieldErrors = {};
  const isAuthError = isTeacherAuthError(error);
  const fallbackMessage = String(requestConfig?.friendlyErrorMessage || "").trim();
  const transportFallbackMessage = isTimeout
    ? "The request took too long. Please check your internet and try again."
    : isNetworkError
      ? "We couldn't reach the server. Please check your internet and try again."
      : "";
  const resolvedFallbackMessage = fallbackMessage || transportFallbackMessage;

  if (isPlainObject(rawPayload)) {
    collectTeacherFieldErrors(rawPayload, fieldErrors);
  }

  const topLevelMessage = extractTeacherErrorMessage(rawPayload, "");
  const shouldUseGenericServerMessage =
    responseStatus >= 500 &&
    !topLevelMessage &&
    !Object.keys(fieldErrors).length;

  return {
    message:
      isAuthError
        ? "Your session has expired. Please log in again."
        : shouldUseGenericServerMessage && resolvedFallbackMessage
          ? resolvedFallbackMessage
        : topLevelMessage ||
          (Object.keys(fieldErrors).length
            ? "Please review the highlighted fields."
            : resolvedFallbackMessage ||
              errorMessage ||
              "Something went wrong. Please try again."),
    fieldErrors,
    isAuthError,
    status: responseStatus,
    raw: rawPayload,
    code: errorCode || null,
    isTimeout,
    isNetworkError,
  };
};

const apiRequest = async (config) => {
  const apiName =
    config?.logName ||
    String(config?.url || "teacher_api")
      .replace(/^\/+|\/+$/g, "")
      .replace(/[{}]/g, "")
      .replace(/\//g, "_") ||
    "teacher_api";
  const authScope = config?.teacherAuth
    ? "teacher"
    : config?.adminAuth
      ? "admin"
      : "public";

  try {
    const response = await teacherApiClient(config);
    logTeacherApiResult({
      api_name: apiName,
      url: config?.url || "",
      method: config?.method || "get",
      status_code: response?.status || 200,
      request_body: config?.data || null,
      query_params: config?.params || null,
      response_body: response?.data || null,
      auth_scope: authScope,
    });
    return response.data;
  } catch (error) {
    const normalizedError = normalizeTeacherError(error, config);
    logTeacherApiResult({
      api_name: apiName,
      url: config?.url || "",
      method: config?.method || "get",
      status_code:
        normalizedError?.status ?? error?.response?.status ?? error?.status ?? 0,
      request_body: config?.data || null,
      query_params: config?.params || null,
      response_body: normalizedError?.raw || error?.response?.data || null,
      error_message:
        normalizedError?.message || error?.message || "Teacher API request failed.",
      error_code: normalizedError?.code || error?.code || null,
      is_timeout: Boolean(normalizedError?.isTimeout),
      is_network_error: Boolean(normalizedError?.isNetworkError),
      auth_scope: authScope,
    });
    throw normalizedError;
  }
};

export const teacherApi = {
  signup: (payload) =>
    apiRequest({
      url: "/teacher_signup/",
      method: "post",
      data: payload,
    }),
  verifyOtp: (payload) =>
    apiRequest({
      url: "/teacher_verify_otp/",
      method: "post",
      data: payload,
    }),
  login: (payload) =>
    apiRequest({
      url: "/teacher_login/",
      method: "post",
      data: payload,
    }),
  loginWithOtp: (payload) =>
    apiRequest({
      url: "/teacher_login_with_otp/",
      method: "post",
      data: payload,
    }),
  verifyLoginOtp: (payload) =>
    apiRequest({
      url: "/teacher_verify_login_otp/",
      method: "post",
      data: payload,
    }),
  getAllTeachers: (filters = {}) =>
    apiRequest({
      url: "/get_all_teachers/",
      method: "get",
      params: filters,
      timeout: PUBLIC_TEACHER_READ_TIMEOUT_MS,
      friendlyErrorMessage:
        "We couldn't load tutors right now. Please check your internet and try again.",
    }),
  getTutors: () =>
    apiRequest({
      url: "/get_all_teachers/",
      method: "get",
      timeout: PUBLIC_TEACHER_READ_TIMEOUT_MS,
      friendlyErrorMessage:
        "We couldn't load tutors right now. Please check your internet and try again.",
    }),
  getTutorDetail: (tutorId) =>
    apiRequest({
      url: `/tutors/${tutorId}/`,
      method: "get",
      timeout: PUBLIC_TEACHER_READ_TIMEOUT_MS,
      friendlyErrorMessage:
        "We couldn't load this tutor profile right now. Please try again shortly.",
    }).then(
      (payload) =>
        extractTeacherRecord(payload) ||
        extractTeacherRecord(payload?.data, payload?.data || {}) ||
        payload?.data ||
        payload
    ),
  getTutorAvailability: (tutorId) =>
    apiRequest({
      url: `/tutors/${tutorId}/availability/`,
      method: "get",
      timeout: PUBLIC_TEACHER_READ_TIMEOUT_MS,
      friendlyErrorMessage:
        "We couldn't load tutor availability right now. Please try again shortly.",
    }),
  getTeacherBookings: () =>
    apiRequest({
      url: "/teacher/bookings/",
      method: "get",
      teacherAuth: true,
      friendlyErrorMessage:
        "We couldn't load your bookings right now. Please try again shortly.",
    }),
  forgotPassword: (payload) =>
    apiRequest({
      url: "/teacher_forgot_password/",
      method: "post",
      data: payload,
    }),
  createAvailability: (payload) =>
    apiRequest({
      url: "/teacher_create_availability/",
      method: "post",
      data: payload,
      teacherAuth: true,
    }),
  getProfile: () =>
    apiRequest({
      url: "/profile/",
      method: "get",
      teacherAuth: true,
    }),
  updateProfile: (payload) =>
    apiRequest({
      url: "/profile/update/",
      method: "post",
      data: payload,
      teacherAuth: true,
    }),
  validateResetPasswordToken: (token) =>
    apiRequest({
      url: "/reset-password/",
      method: "get",
      params: { token },
    }),
  submitResetPassword: (payload) =>
    apiRequest({
      url: "/reset-password/submit/",
      method: "post",
      data: payload,
    }),
  createTeacherAddress: (payload, adminAuthContext) =>
    apiRequest({
      url: "/admin_create_teacher_address/",
      method: "post",
      data: payload,
      adminAuth: adminAuthContext || true,
    }),
};
